/**
 * WebSocket hook for omnichannel real-time updates
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  OmnichannelAuthResponse,
  OmnichannelDraftCreatedEvent,
  OmnichannelNewMessageEvent,
} from '../types/omnichannel';

const SOCKET_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export interface OmnichannelSocketState {
  isConnected: boolean;
  isAuthenticated: boolean;
  isSubscribed: boolean;
  error: string | null;
}

export interface OmnichannelSocketCallbacks {
  onNewMessage?: (event: OmnichannelNewMessageEvent) => void;
  onDraftCreated?: (event: OmnichannelDraftCreatedEvent) => void;
  onMessageUpdated?: (event: any) => void;
  onDraftSent?: (event: any) => void;
}

export function useOmnichannelSocket(callbacks?: OmnichannelSocketCallbacks) {
  const socketRef = useRef<Socket | null>(null);
  const callbacksRef = useRef(callbacks);
  const [state, setState] = useState<OmnichannelSocketState>({
    isConnected: false,
    isAuthenticated: false,
    isSubscribed: false,
    error: null,
  });

  // Keep callbacks ref updated without causing re-renders
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('omnichannel_unsubscribe', {});
      socketRef.current.disconnect();
      socketRef.current = null;
      setState({
        isConnected: false,
        isAuthenticated: false,
        isSubscribed: false,
        error: null,
      });
    }
  }, []);

  // Connect and authenticate
  const connect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setState(prev => ({ ...prev, error: 'No authentication token' }));
      return;
    }

    // Don't reconnect if already connected
    if (socketRef.current?.connected) {
      return;
    }

    // Cleanup existing socket if any
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    // Create socket connection
    const socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('Omnichannel socket connected');
      setState(prev => ({ ...prev, isConnected: true, error: null }));

      // Authenticate as operator
      socket.emit('omnichannel_auth', { token });
    });

    socket.on('disconnect', (reason) => {
      console.log('Omnichannel socket disconnected:', reason);
      setState(prev => ({
        ...prev,
        isConnected: false,
        isAuthenticated: false,
        isSubscribed: false,
      }));
    });

    socket.on('connect_error', (error) => {
      console.error('Omnichannel socket connection error:', error);
      setState(prev => ({ ...prev, error: 'Connection failed' }));
    });

    // Authentication events
    socket.on('omnichannel_auth_response', (data: OmnichannelAuthResponse) => {
      if (data.status === 'success') {
        console.log('Omnichannel authenticated:', data.user_email);
        setState(prev => ({ ...prev, isAuthenticated: true, error: null }));

        // Auto-subscribe after authentication
        socket.emit('omnichannel_subscribe', {});
      }
    });

    socket.on('omnichannel_auth_error', (data: { error: string; message: string }) => {
      console.error('Omnichannel auth error:', data);
      setState(prev => ({ ...prev, error: data.message }));
    });

    // Subscription events
    socket.on('omnichannel_subscribed', () => {
      console.log('Subscribed to omnichannel updates');
      setState(prev => ({ ...prev, isSubscribed: true }));
    });

    socket.on('omnichannel_unsubscribed', () => {
      console.log('Unsubscribed from omnichannel updates');
      setState(prev => ({ ...prev, isSubscribed: false }));
    });

    socket.on('omnichannel_error', (data: { error: string; message: string }) => {
      console.error('Omnichannel error:', data);
      setState(prev => ({ ...prev, error: data.message }));
    });

    // Message events - use ref to avoid stale closures
    socket.on('omnichannel_new_message', (data: OmnichannelNewMessageEvent) => {
      console.log('New message received:', data);
      callbacksRef.current?.onNewMessage?.(data);
    });

    socket.on('omnichannel_draft_created', (data: OmnichannelDraftCreatedEvent) => {
      console.log('Draft created:', data);
      callbacksRef.current?.onDraftCreated?.(data);
    });

    socket.on('omnichannel_message_updated', (data: any) => {
      console.log('Message updated:', data);
      callbacksRef.current?.onMessageUpdated?.(data);
    });

    socket.on('omnichannel_draft_sent', (data: any) => {
      console.log('Draft sent:', data);
      callbacksRef.current?.onDraftSent?.(data);
    });

  }, []);

  // Auto-connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Reconnect if token changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        disconnect();
        if (e.newValue) {
          connect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    socket: socketRef.current,
  };
}


